import { useForm } from 'react-hook-form';
import { useState } from 'react';
import {
  applySpec,
  chain,
  compose,
  defaultTo,
  filter,
  find,
  identity,
  includes,
  isEmpty,
  isNil,
  length,
  lensProp,
  map,
  modify,
  none,
  not,
  omit,
  over,
  pathOr,
  pick,
  pluck,
  prop,
  propEq,
  propOr,
  take
} from 'ramda';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import useSaveSimulationPricingsIndividualHealthGranteesMutation
  from './useSaveSimulationPricingsIndividualHealthGranteesMutation';
import formatExtraCommission from '../extra/formatExtraCommission.js';

const useGuaranteeLevelsForm = ({
  offer,
  getMiddleNumber,
  setDisplayError,
  selectedTab,
  grantees
}) => {
  const [loading, setLoading] = useState(false);
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      paymentInformations: {
        paymentFrequency: 'MONTHLY'
      },
      ...formatExtraCommission(offer)
    }
  });

  const maxNumberOfProducts = getMiddleNumber(
    'NIVEAUXGARANTIESETTARIFS_GENERAL_MAX_NUMBER_OF_PRODUCTS',
    1
  );

  const { onFormSubmit } = useSaveSimulationPricingsIndividualHealthGranteesMutation();

  const {
    processHandlers,
    context
  } = useProcess();

  const $onClickPrev = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const packs = compose(
    take(length(grantees)), // Dans certains cas (exemple : suppression d'un bénéficiaire), on peut se retrouver avec plus de packs que de grantees
    defaultTo([])
  )(form.watch('packs'));

  const selectedPricings = map(compose(filter(prop('isPreSelected')), propOr([], 'pricings')))(
    packs
  );

  const commission = form.watch('packs.0.commission');

  const isValid = none(isEmpty, selectedPricings);

  const currentPricings = pathOr([], [selectedTab, 'pricings'], packs);

  const findRecommandedMessage = (field) => compose(
    defaultTo({}),
    find(pricing => !isEmpty(prop(field, pricing)))
  );

  const createMessageExtractor = (fieldName, transform = identity) =>
    compose(
      propOr('', fieldName),
      findRecommandedMessage(fieldName),
      transform
    );

  const recommendedMessageInfo = applySpec({
    recommendedProductMessage: createMessageExtractor('recommendedProductMessage'),
    recommendedOptionMessage: createMessageExtractor(
      'recommendedOptionMessage',
      chain(prop('supplementaryCovers'))
    )
  })(currentPricings);

  const onSubmit = (values) => {
    if (loading) return;
    setLoading(true);

    const cleanCommission = map(over(lensProp('commission'), pick(['rate'])));

    const formattedValues = compose(
      modify('packs', cleanCommission),
      omit(['error', 'paymentInformations'])
    )(values);

    const offerSelected = compose(
      find(propEq('isPreSelected', true)),
      chain(propOr([], 'pricings')),
      propOr([], 'packs')
    )(values);

    const supplementaryCoverIsMandatory = propOr([], 'packs', values).some((pack) => {
      const pricings = propOr([], 'pricings', pack);
      const currentOfferSelected = find(propEq('isPreSelected', true), pricings);

      if (!currentOfferSelected) return false;

      const requiredSupplementaryCover = compose(
        not,
        isNil,
        find(includes('OBLIGATOIRE')),
        pluck('supplementarySelectionMode'),
        propOr([], 'supplementaryCovers')
      )(currentOfferSelected);

      return requiredSupplementaryCover
        ? compose(
          isNil,
          find(propEq('isPreSelected', true)),
          propOr([], 'supplementaryCovers')
        )(currentOfferSelected)
        : false;
    });

    if (!offerSelected || supplementaryCoverIsMandatory) {
      setDisplayError({
        display: true,
        message: !offerSelected
          ? 'Veuillez saisir au moins une offre pour poursuivre le parcours'
          : 'Veuillez saisir au moins un renfort pour poursuivre le parcours'
      });

      return setTimeout(() => {
        setLoading(false);
        return setDisplayError({ display: false });
      }, 3000);
    }

    onFormSubmit(formattedValues, (ok, error) => {
      if (ok && !error) {
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      } else {
        setLoading(false);
      }
    });
  };

  return {
    form,
    maxNumberOfProducts,
    onFormSubmit,
    $onClickPrev,
    onSubmit,
    isValid,
    commission,
    currentPricings,
    recommendedMessageInfo
  };
};

export default useGuaranteeLevelsForm;
